import { useEffect, useState, useMemo } from "react";
import { useLocation, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PhotoCarousel from "@/components/PhotoCarousel";
import { Loader2, Globe, ChevronDown, Star, Heart, Circle, Square, Diamond, Triangle, Hexagon } from "lucide-react";
import SocialLinks from "@/components/SocialLinks";
import { PublicQrPrice } from "@/components/PublicQrPrice";
import { PictogramImage } from "@/components/PictogramImage";
import { PUBLIC_PALETTE } from "@/lib/publicPalette";
import { groupPictograms } from "@/lib/pictogramGroups";

const RATING_ICONS = {
  star: Star, heart: Heart,
  circle: Circle, square: Square, diamond: Diamond, triangle: Triangle, hexagon: Hexagon,
};

const LANGS = [
  { code: "it", label: "IT" },
  { code: "en", label: "EN" },
  { code: "fr", label: "FR" },
  { code: "es", label: "ES" },
  { code: "de", label: "DE" },
];

const L = {
  it: { brand: "Brand", code: "Codice", quantity: "Quantità", description: "Descrizione",
        price: "Prezzo di vendita",
        characteristics: "Caratteristiche", usage: "Modo d'uso", warnings: "Avvertenze",
        pictograms: "Smaltimento", inci: "Ingredienti (INCI)", barcode: "Codice a barre",
        safety: "Scheda sicurezza", packaging_certification: "Documento certificazione packaging", certification: "Certificazione", view: "Visualizza",
        buy_now: "Acquista ora", translating: "Traduzione in corso..." },
  en: { brand: "Brand", code: "Code", quantity: "Quantity", description: "Description",
        price: "Sale price",
        characteristics: "Characteristics", usage: "How to use", warnings: "Warnings",
        pictograms: "Disposal", inci: "Ingredients (INCI)", barcode: "Barcode",
        safety: "Safety sheet", packaging_certification: "Packaging certification document", certification: "Certification", view: "View",
        buy_now: "Buy now", translating: "Translating..." },
  fr: { brand: "Marque", code: "Code", quantity: "Quantité", description: "Description",
        price: "Prix de vente",
        characteristics: "Caractéristiques", usage: "Mode d'emploi", warnings: "Avertissements",
        pictograms: "Élimination", inci: "Ingrédients (INCI)", barcode: "Code-barres",
        safety: "Fiche de sécurité", packaging_certification: "Document de certification de l’emballage", certification: "Certification", view: "Consulter",
        buy_now: "Acheter", translating: "Traduction..." },
  es: { brand: "Marca", code: "Código", quantity: "Cantidad", description: "Descripción",
        price: "Precio de venta",
        characteristics: "Características", usage: "Modo de uso", warnings: "Advertencias",
        pictograms: "Eliminación", inci: "Ingredientes (INCI)", barcode: "Código de barras",
        safety: "Ficha de seguridad", packaging_certification: "Documento de certificación del embalaje", certification: "Certificación", view: "Ver",
        buy_now: "Comprar", translating: "Traduciendo..." },
  de: { brand: "Marke", code: "Code", quantity: "Menge", description: "Beschreibung",
        price: "Verkaufspreis",
        characteristics: "Eigenschaften", usage: "Anwendung", warnings: "Warnhinweise",
        pictograms: "Entsorgung", inci: "Inhaltsstoffe (INCI)", barcode: "Strichcode",
        safety: "Sicherheitsdatenblatt", packaging_certification: "Dokument zur Verpackungszertifizierung", certification: "Zertifizierung", view: "Ansehen",
        buy_now: "Kaufen", translating: "Übersetzung läuft..." },
};

export default function PublicProduct() {
  const { slug } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [lang, setLang] = useState("it");
  const [translating, setTranslating] = useState(false);
  const [translations, setTranslations] = useState({});
  const [udmConv, setUdmConv] = useState(null);
  const [showLangs, setShowLangs] = useState(false);

  useEffect(() => {
    document.title = "Cosmely";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    api.get(`/public/products/${slug}`, { params: { _refresh: Date.now() } })
      .then(r => {
        if (r.data?.disabled) { nav("/disabled"); return; }
        setData(r.data);
      })
      .catch(() => nav("/disabled"));
  }, [slug, nav, location.key]);

  const t = useMemo(() => L[lang] || L.it, [lang]);

  const switchLang = async (newLang) => {
    setShowLangs(false);
    if (newLang === lang) return;
    setLang(newLang);
    const cacheKey = `cosm_tr_${slug}_${newLang}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      const obj = JSON.parse(cached);
      setTranslations(obj.translations);
      setUdmConv(obj.udm_conversion);
      return;
    }
    if (newLang === "it") { setTranslations({}); setUdmConv(null); return; }
    setTranslating(true);
    try {
      const r = await api.post(`/public/products/${slug}/translate`, { target_lang: newLang });
      setTranslations(r.data.translations || {});
      setUdmConv(r.data.udm_conversion);
      sessionStorage.setItem(cacheKey, JSON.stringify(r.data));
    } catch { /* silent fallback */ }
    finally { setTranslating(false); }
  };

  if (!data) {
    return (
      <div className="public-page flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#8A8579]"/>
      </div>
    );
  }

  const tx = (key, original) => (lang === "it" ? original : (translations[key] || original));
  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const ratingIconKey = data.brand?.rating_icon || "star";
  const ratingIconUrl = data.brand?.rating_icon_url || null;
  const youtubeId = data.youtube_url ? extractYoutubeId(data.youtube_url) : null;
  const enabledBlocks = data.blocks || [];

  // Each renderBlock returns either a .pp-block element or null so that
  // the hairline separators (CSS `.pp-frame > .pp-block + .pp-block`)
  // only appear between rendered siblings.
  const renderBlock = (b) => {
    const k = b.block_key;
    switch (k) {
      case "photos": {
        if (!data.photos?.length) return null;
        return (
          <div key={k} className="pp-block pp-block--media" data-testid="block-photos">
            <PhotoCarousel photos={data.photos} apiBase={apiBase} alt={data.name} />
          </div>
        );
      }
      case "brand": {
        if (!data.brand?.name && !data.brand?.logo_url) return null;
        return (
          <div key={k} className="pp-block pp-brand-mark" data-testid="block-brand">
            {data.brand.logo_url ? (
              <img src={`${apiBase}${data.brand.logo_url}`} alt={data.brand.name || ""}/>
            ) : (
              <span className="pp-eyebrow">{data.brand.name}</span>
            )}
          </div>
        );
      }
      case "name":
        return (
          <div key={k} className="pp-block" data-testid="block-name">
            <h1 className="pp-title">{data.name}</h1>
            {(data.subcategory || data.category) && (
              <div className="pp-subtitle" data-testid="block-name-category">
                {data.subcategory || data.category}
              </div>
            )}
          </div>
        );
      case "code":
        return data.code ? (
          <div key={k} className="pp-block" data-testid="block-code">
            <div className="pp-meta-row"><span>Codice <strong>{data.code}</strong></span></div>
          </div>
        ) : null;
      case "quantity":
        return data.quantity_value ? (
          <div key={k} className="pp-block" data-testid="block-quantity">
            <div className="pp-meta-row">
              <span>{t.quantity} <strong>{data.quantity_value} {data.udm}{udmConv ? ` / ${udmConv}` : ""}</strong></span>
            </div>
          </div>
        ) : null;
      case "price":
        return <PublicQrPrice key={k} block={b} value={data.price_value} currency={data.price_currency} label={t.price} />;
      case "description": {
        const txt = tx("description", data.description);
        return txt ? (
          <div key={k} className="pp-block" data-testid="block-description">
            <h3 className="pp-block__heading">{t.description}</h3>
            <div className="pp-block__body prose-rt" dangerouslySetInnerHTML={{ __html: txt }}/>
          </div>
        ) : null;
      }
      case "characteristics": {
        const filled = (data.characteristics || []).filter(c => Number(c.value) > 0);
        return filled.length ? (
          <div key={k} className="pp-block" data-testid="block-characteristics">
            <h3 className="pp-block__heading">{t.characteristics}</h3>
            <div className="pp-block__body">
              {filled.map(c => (
                <div key={c.name} className="pp-char-row">
                  <span>{tx(`char_${c.name}`, c.name)}</span>
                  <Rating value={c.value} brandColor="#000000"
                          iconKey={ratingIconKey} iconUrl={ratingIconUrl}/>
                </div>
              ))}
            </div>
          </div>
        ) : null;
      }
      case "usage": {
        const txt = tx("usage", data.usage);
        return txt ? (
          <div key={k} className="pp-block" data-testid="block-usage">
            <h3 className="pp-block__heading">{t.usage}</h3>
            <div className="pp-block__body prose-rt" dangerouslySetInnerHTML={{__html: txt}}/>
          </div>
        ) : null;
      }
      case "warnings": {
        const txt = tx("warnings", data.warnings);
        return txt ? (
          <div key={k} className="pp-block" data-testid="block-warnings">
            <h3 className="pp-block__heading">{t.warnings}</h3>
            <div className="pp-warning-body prose-rt" dangerouslySetInnerHTML={{__html: txt}}/>
          </div>
        ) : null;
      }
      case "pictograms":
        return data.pictograms?.length ? (() => {
          const groups = groupPictograms(data.pictogram_categories || [], data.pictograms, t.pictograms)
            .map(g => ({ ...g, name: g.id ? tx(`pcat_${g.id}`, g.name) : g.name }));
          // Render each category as its own .pp-block so it shares the same
          // heading style + inter-block border as Avvertenze / Modo d'uso.
          return groups.map(g => (
            <div key={`${k}-${g.id || "none"}`}
                 className="pp-block"
                 data-testid={g.id ? `block-pictogram-group-${g.id}` : "block-pictograms"}>
              <h3 className="pp-block__heading">{g.name}</h3>
              <div className="grid grid-cols-3 gap-4">
                {g.items.map(p => (
                  <div key={p.id} className="pp-picto">
                    <div className="pp-picto__icon-box">
                      {p.icon_url && <PictogramImage pictogram={p} className="w-full h-full"
                        alt={tx(`picto_${p.id}`, p.name)} testId={`public-qr-picto-image-${p.id}`} />}
                    </div>
                    <span className="pp-picto__label" data-testid={`public-qr-picto-label-${p.id}`}>{tx(`picto_${p.id}`, p.name)}</span>
                  </div>
                ))}
              </div>
            </div>
          ));
        })() : null;
      case "inci":
        return data.ingredients?.length ? (
          <div key={k} className="pp-block" data-testid="block-inci">
            <h3 className="pp-block__heading">{t.inci}</h3>
            <div className="pp-inci">{data.ingredients.join(", ")}.</div>
          </div>
        ) : null;
      case "barcode":
        return data.barcode_number ? (
          <div key={k} className="pp-block" data-testid="block-barcode">
            <div className="pp-meta-row">
              <span>{t.barcode} <strong className="font-mono">{data.barcode_number}</strong></span>
            </div>
          </div>
        ) : null;
      case "safety_sheet":
        return data.safety_sheet_url ? (
          <div key={k} className="pp-block" data-testid="block-safety">
            <button onClick={() => window.location.href = `/p/${slug}/safety`}
                    className="pp-btn"
                    data-testid="safety-view-btn">
              {t.safety} — {t.view}
            </button>
          </div>
        ) : null;
      case "packaging_certification":
        return data.packaging_certifications?.length ? (
          <div key={k} className="pp-block" data-testid="block-packaging-certification">
            <div className="space-y-2">
              {data.packaging_certifications.map(doc => (
                <button key={doc.id} type="button"
                        onClick={() => window.location.href = `/p/${slug}/packaging/${encodeURIComponent(doc.id)}`}
                        className="pp-btn min-w-0 break-words" style={{ textTransform: "none", letterSpacing: "0.06em" }}
                        data-testid={`packaging-public-view-${doc.id}`}>
                  {t.view} {t.certification} {doc.name.trim().replace(/^(certificazione|certification|certificación|zertifizierung)(?:\s+|\s*[-:–]\s*|$)/i, "")}
                </button>
              ))}
            </div>
          </div>
        ) : null;
      case "video":
        return youtubeId ? (
          <div key={k} className="pp-block pp-block--media" data-testid="block-video">
            <div className="aspect-video bg-black">
              <iframe src={`https://www.youtube.com/embed/${youtubeId}`}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen title="Video"/>
            </div>
          </div>
        ) : null;
      case "buy_now":
        return data.website_url ? (
          <div key={k} className="pp-block" data-testid="block-buy-now">
            <a href={data.website_url}
               target="_blank"
               rel="noopener noreferrer nofollow"
               className="pp-btn pp-btn--filled"
               data-testid="buy-now-btn">
              {t.buy_now}
            </a>
          </div>
        ) : null;
      case "social": {
        const b = data.brand || {};
        const hasAny = [b.instagram_url, b.facebook_url, b.linkedin_url, b.x_url]
          .some(u => (u || "").trim().length > 0);
        if (!hasAny) return null;
        return (
          <div key={k} className="pp-block pp-social" data-testid="block-social">
            <SocialLinks urls={{
              instagram_url: b.instagram_url,
              facebook_url: b.facebook_url,
              linkedin_url: b.linkedin_url,
              x_url: b.x_url,
            }} size="md" className="justify-center gap-6"/>
          </div>
        );
      }
      default: return null;
    }
  };

  return (
    <div className="public-page"
          data-testid="public-product"
          style={PUBLIC_PALETTE}>
      {/* — Header — */}
      <header className="sticky top-0 z-30 backdrop-blur"
              style={{
                borderBottom: `1px solid var(--brand)`,
                background: "var(--surface)",
              }}>
        <div className="flex justify-between items-center px-5 py-3">
          <div className="text-[10px] tracking-[0.3em] uppercase font-medium"
               style={{ color: "var(--brand)" }}>Cosmely</div>
          <div className="relative">
            <button onClick={()=>setShowLangs(s=>!s)}
                    className="text-[11px] tracking-[0.25em] uppercase flex items-center gap-1.5"
                    style={{ color: "var(--brand)" }}
                    data-testid="lang-selector">
              <Globe className="w-3.5 h-3.5" strokeWidth={1.5}/>
              {lang.toUpperCase()}
              <ChevronDown className="w-3 h-3" strokeWidth={1.5}/>
            </button>
            {showLangs && (
              <div className="absolute right-0 top-full mt-2 z-50 min-w-[80px]"
                   style={{ border: `1px solid var(--brand)`, background: "var(--surface)" }}
                   data-testid="lang-dropdown">
                {LANGS.map(l => (
                  <button key={l.code} onClick={()=>switchLang(l.code)}
                           className={`block px-4 py-2.5 text-[11px] tracking-[0.25em] uppercase w-full text-left transition-colors pp-lang-opt ${l.code === lang ? "font-semibold" : ""}`}
                           data-testid={`lang-option-${l.code}`}>
                    {l.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        {translating && (
          <div className="text-[10px] tracking-[0.3em] uppercase py-2 text-center flex items-center justify-center gap-2"
               style={{ background: "var(--brand)", color: "var(--surface)" }}
               data-testid="translating-banner">
            <Loader2 className="w-3 h-3 animate-spin"/> {t.translating}
          </div>
        )}
      </header>

      {/* — Outer editorial frame; blocks rendered in the exact QR-layout order — */}
      <article className="pb-6">
        <div className="pp-frame">
          {enabledBlocks.map(renderBlock).filter(Boolean)}
        </div>
      </article>
    </div>
  );
}

/**
 * Brand-aware 5-step rating indicator.
 *
 * - Custom uploaded icon (`rating_icon_url`) wins when present (rendered as
 *   <img>, brand-coloured via CSS mask isn't possible cross-browser for
 *   arbitrary PNGs, so we just adjust opacity for inactive steps).
 * - Otherwise picks a lucide icon from RATING_ICONS by the brand's
 *   `rating_icon` field (heart for ONELOVE, star/leaf/droplet/sparkle for
 *   others). Active icons are filled with the brand colour for a
 *   tactile "level" effect.
 */
function Rating({ value, brandColor, iconKey, iconUrl }) {
  const steps = [1, 2, 3, 4, 5];
  if (iconUrl) {
    // Custom uploaded brand icon: opacity-based level indicator
    return (
      <div className="flex gap-[3px] items-center" data-testid="rating-custom">
        {steps.map(v => (
          <img key={v}
                src={iconUrl}
                alt=""
                className="w-4 h-4 object-contain"
                style={{ opacity: v <= value ? 1 : 0.18 }}/>
        ))}
      </div>
    );
  }
  const Icon = RATING_ICONS[iconKey] || Star;
  return (
    <div className="flex gap-[3px] items-center" data-testid={`rating-${iconKey}`}>
      {steps.map(v => {
        const active = v <= value;
        return (
          <Icon key={v}
                className="w-4 h-4"
                strokeWidth={1.5}
                style={{
                  color: active ? brandColor : "#737373",
                  fill: active ? brandColor : "transparent",
                }}/>
        );
      })}
    </div>
  );
}

function extractYoutubeId(url) {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return u.pathname.slice(1);
    return u.searchParams.get("v");
  } catch { return null; }
}
